type Category = 'DocumentRequest' | 'TitleMaintenance' | 'TitleReleaseStatus';

type MappingConfig = {
    [key in Category]: {
        default: Record<string, string>;
        serviceLoaner: Record<string, string>;
    };
};

const TASK_MAPPING: MappingConfig = {
    DocumentRequest: {
        default: {
            POA: '91',
            StateSpecificForm: '59',
            LienRelease: '10',
            SOC: '70',
            AOC: '12',
        },
        serviceLoaner: {
            POA: '74',
            StateSpecificForm: '71',
            LienRelease: '62',
            SOC: '72',
            AOC: '64',
        },
    },
    TitleMaintenance: {
        default: {
            DuplicateTitle: '16',
            StateTransfer: '136',
            DiscrepantTitle: '18',
            VINChange: '55',
            TOE: '76',
        },
        serviceLoaner: {
            DuplicateTitle: '68',
            StateTransfer: '75',
            DiscrepantTitle: '69',
            VINChange: '70',
            TOE: '73',
        },
    },
    TitleReleaseStatus: {
        default: {
            '1-34': '15',
            '35-60': '11',
            '61-90': '13',
            '91+': '192',
        },
        serviceLoaner: {
            '1-34': '67',
            '35-60': '63',
            '61-90': '65',
            '91+': '76',
        },
    },
};

// Maps UI display values to the mapping keys used in TASK_MAPPING
const CATEGORY_KEY_MAP: Record<string, Category> = {
    'Document Request': 'DocumentRequest',
    'Title Maintenance': 'TitleMaintenance',
    'Title Release Status': 'TitleReleaseStatus',
};

const REQUEST_TYPE_KEY_MAP: Record<string, string> = {
    // Document Request types
    'Power of Attorney (POA)': 'POA',
    'Power of Attorney (POA) - Titling and Licensing': 'POA',
    'Power of Attorney (POA) - Duplicate and Reassignment': 'POA',
    'Bulk Power of Attorney (POA)': 'POA',
    'State Specific Form': 'StateSpecificForm',
    'Lien Release': 'LienRelease',
    'Substitution of Collateral (SOC)': 'SOC',
    'Affidavit of Correction (AOC)': 'AOC',
    // Title Maintenance types
    'Duplicate Title': 'DuplicateTitle',
    'State Transfer': 'StateTransfer',
    'Discrepant Title': 'DiscrepantTitle',
    'VIN Change': 'VINChange',
    'Transfer of Equity (TOE)': 'TOE',
    // Title Release Status types
    '1-34 days since purchase date': '1-34',
    '35-60 days since purchase date': '35-60',
    '61-90 days since purchase date': '61-90',
    '91+ days since purchase date': '91+',
};

interface ITaskMappingParams {
    reasonForRequest: string;
    selectedRequestType: string;
    accountType: string;
    isResubmission: boolean;
    isFieldUser: boolean;
    specialInstructions: string;
}

interface ITaskMappingResult {
    taskId: string;
    specialInstructions: string;
}

export function resolveTaskMapping(
    params: ITaskMappingParams,
): ITaskMappingResult {
    const {
        reasonForRequest,
        selectedRequestType,
        accountType,
        isResubmission,
        isFieldUser,
        specialInstructions,
    } = params;

    const isServiceLoaner = accountType === 'Service Loaner';

    // Previous request (resubmission) override logic
    if (isResubmission) {
        const taskId = isServiceLoaner
            ? isFieldUser
                ? '77'
                : '66'
            : isFieldUser
            ? '21'
            : '14';

        const category = CATEGORY_KEY_MAP[reasonForRequest] || reasonForRequest;
        const requestType =
            REQUEST_TYPE_KEY_MAP[selectedRequestType] || selectedRequestType;

        const updatedNote =
            `${specialInstructions}:Reason for request :${category}` +
            ` Request Type: ${requestType}`;

        return { taskId, specialInstructions: updatedNote };
    }

    const isLeasePoaRequest =
        accountType === 'Lease' &&
        reasonForRequest === 'Document Request' &&
        selectedRequestType in REQUEST_TYPE_KEY_MAP &&
        REQUEST_TYPE_KEY_MAP[selectedRequestType] === 'POA';

    if (isLeasePoaRequest) {
        return {
            taskId: isFieldUser ? '21' : '91',
            specialInstructions,
        };
    }

    // Standard mapping
    const categoryKey = CATEGORY_KEY_MAP[reasonForRequest];
    const requestTypeKey =
        REQUEST_TYPE_KEY_MAP[selectedRequestType] || selectedRequestType;

    const taskId = getTaskId(categoryKey, requestTypeKey, isServiceLoaner);

    return { taskId: taskId || '', specialInstructions };
}

function getTaskId(
    category: Category | undefined,
    selectedType: string,
    isServiceLoaner: boolean,
): string | null {
    if (!category) return null;

    const categoryConfig = TASK_MAPPING[category];
    if (!categoryConfig) return null;

    const map = isServiceLoaner
        ? categoryConfig.serviceLoaner
        : categoryConfig.default;

    return map[selectedType] ?? null;
}
